import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopBar from '../components/TopBar';
import { quotesService } from '../lib/services';
import { useToast } from '../lib/toast';

const STATUS_TONE = {
  DRAFT:      '',
  SENT:       'accent',
  ACCEPTED:   'success',
  DECLINED:   'danger',
  EXPIRED:    'warning',
  SUPERSEDED: '',
  CANCELLED:  'danger',
};

const STATUS_OPTIONS = ['DRAFT', 'SENT', 'ACCEPTED', 'DECLINED', 'EXPIRED', 'CANCELLED'];

export function fmtMoneyCents(c) {
  if (c == null) return '—';
  const neg = c < 0;
  const s = '$' + Math.abs(Math.round(c / 100)).toLocaleString('en-AU');
  return neg ? '-' + s : s;
}

function unwrapList(resp) {
  return resp?.data?.content ?? resp?.content ?? (Array.isArray(resp?.data) ? resp.data : [])
      ?? (Array.isArray(resp) ? resp : []);
}

export default function Quotes() {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page: 0, size: 100 };
      if (statusFilter) params.status = statusFilter;
      const resp = await quotesService.list(params);
      setRows(unwrapList(resp));
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load quotes');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => (r.quoteNumber || '').toLowerCase().includes(q));
  }, [rows, search]);

  const stats = useMemo(() => {
    const open = rows.filter((r) => ['DRAFT', 'SENT'].includes(r.status)).length;
    const accepted = rows.filter((r) => r.status === 'ACCEPTED').length;
    const value = rows
      .filter((r) => ['DRAFT', 'SENT', 'ACCEPTED'].includes(r.status))
      .reduce((sum, r) => sum + (r.totalCents || 0), 0);
    return { total: rows.length, open, accepted, value };
  }, [rows]);

  return (
    <>
      <TopBar
        crumbs={['Deal Desk']}
        right={
          <>
            <button className="btn small" onClick={load} title="Refresh">↻ Refresh</button>
            <button className="btn small primary" onClick={() => navigate('/quotes/new')}>+ New quote</button>
          </>
        }
      />
      <div className="app-content" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div className="kpi-strip" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
          <Kpi label="Total quotes" value={stats.total} />
          <Kpi label="Open" value={stats.open} tone="accent" />
          <Kpi label="Accepted" value={stats.accepted} tone="success" />
          <Kpi label="Open pipeline value" value={fmtMoneyCents(stats.value)} />
        </div>

        <div className="crm-toolbar" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            className="crm-search"
            placeholder="Search quote number…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: '1 1 260px', minWidth: 200 }}
          />
          <select className="btn small" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <div className="card" style={{ padding: 0 }}>
          {loading && <Empty icon="⏳" title="Loading quotes…" />}
          {!loading && error && (
            <Empty icon="⚠" title="Couldn't load quotes" desc={error} action={<button className="btn small" onClick={load}>Try again</button>} />
          )}
          {!loading && !error && filtered.length === 0 && (
            <Empty icon="🧾" title="No quotes yet" desc="Create a quote from a deal, a vehicle, or start a fresh one." />
          )}
          {!loading && !error && filtered.length > 0 && (
            <table className="data-table" style={{ width: '100%', tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: 150 }} />
                <col />
                <col style={{ width: 130 }} />
                <col style={{ width: 130 }} />
                <col style={{ width: 120 }} />
                <col style={{ width: 120 }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Quote #</th>
                  <th>Status</th>
                  <th>Drive-away</th>
                  <th>Balance</th>
                  <th>Valid until</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((q) => (
                  <tr key={q.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/quotes/${q.id}`)}>
                    <td className="mono" style={{ fontWeight: 600 }}>{q.quoteNumber}</td>
                    <td><span className={`pill ${STATUS_TONE[q.status] || ''} filled`}>{q.status}</span></td>
                    <td>{fmtMoneyCents(q.totalCents)}</td>
                    <td>{fmtMoneyCents(q.balancePayableCents)}</td>
                    <td className="text-muted">{q.validUntil || '—'}</td>
                    <td><button className="btn small" onClick={(e) => { e.stopPropagation(); navigate(`/quotes/${q.id}`); }}>Open</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}

function Kpi({ label, value, tone }) {
  const color = tone === 'success' ? 'var(--success)' : tone === 'accent' ? 'var(--accent-primary)' : 'var(--text-primary)';
  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div className="card-label">{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
    </div>
  );
}

function Empty({ icon, title, desc, action }) {
  return (
    <div className="empty-state" style={{ padding: 40 }}>
      <div className="icon">{icon}</div>
      <div className="title">{title}</div>
      {desc && <div className="desc">{desc}</div>}
      {action && <div style={{ marginTop: 12 }}>{action}</div>}
    </div>
  );
}
